import { useState } from "react";
import { format } from "date-fns";
import {
    Box,
    Typography,
    Button,
    IconButton,
    Tooltip,
    Stack,
    CircularProgress,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    ToggleButtonGroup,
    ToggleButton,
} from "@mui/material";
import { Edit, CheckCircle, History, Sync, ViewList, PlaylistAdd } from "@mui/icons-material";
import CrewEditor from "./CrewEditor";
import InventoryEditor from "./InventoryEditor";
import BulkInventoryEditor from "./BulkInventoryEditor";
import SessionInfoTable from "./SessionInfoTable";
import CrewList from "./CrewList";
import InventoryList from "./InventoryList";


const emptyInventoryRow = () => ({
    productId: "", 
    productCode: "", 
    productName: "", 
    unit: "",
    category: "", 
    unitPrice: 0, 
    commissionRate: 0, 
    allocatedQty: 1,
});


const FlightDetailDialog = ({
    open, 
    onClose,
    detailFlight, 
    detailSession,
    isOnline,
    syncingSession, 
    onSyncSession,
    editMode, 
    setEditMode,
    crewEditMode, 
    setCrewEditMode,
    inventoryRows, 
    setInventoryRows, 
    products,
    editRows, 
    setEditRows,
    crewRows, 
    setCrewRows, 
    availableCrew,
    submitting,
    onStartSession, 
    onOpenEdit, 
    onOpenCrewEdit,
    onSaveInventory, 
    onSaveCrew,
    onCloseSession, 
    onReopenSession,
    onUpdateInventoryRow, 
    onUpdateEditRow, 
    onUpdateCrewRow,
}) => {
    const [closeDialogOpen, setCloseDialogOpen] = useState(false);
    const [inventoryMode, setInventoryMode]     = useState("bulk"); // "manual" | "bulk"

    return (
        <>
            <Dialog
                fullWidth
                maxWidth="sm"
                open={open}
                onClose={() => { 
                    onClose(); 
                    setEditMode(false); 
                    setCrewEditMode(false); 
                }}
            >
                <DialogTitle sx={{ pb: 0.5 }}>
                    <Stack 
                        direction="row" 
                        justifyContent="space-between" 
                        alignItems="flex-start"
                    >
                        <Box>
                            <Typography variant="body2">
                                {detailFlight?.flightDesc} {detailFlight?.dep}-{detailFlight?.arr}
                                {" "}{detailFlight?.acType || "-"}{" "}{detailFlight?.flightReg || "-"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {detailFlight?.flightDate ? format(new Date(detailFlight.flightDate), "dd/MM/yyyy") : ""}
                                {" "}{detailFlight?.etd || detailFlight?.std || "-"} - {detailFlight?.eta || detailFlight?.sta || "-"}
                            </Typography>
                            {detailSession?.syncStatus && detailSession.syncStatus !== "synced" && (
                                <Chip
                                    size="small"
                                    label="Pending sync"
                                    color="warning"
                                    variant="outlined"
                                    sx={{ mt: 0.5, fontSize: "0.65rem", height: 20 }}
                                />
                            )}
                        </Box>
                        {detailSession && (
                            <Tooltip title={!isOnline ? "Offline" : syncingSession ? "Syncing…" : "Sync with server"}>
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={onSyncSession}
                                        disabled={!isOnline || syncingSession}
                                        sx={{ mt: -0.5 }}
                                    >
                                        {syncingSession ? <CircularProgress size={16} /> : <Sync fontSize="small" />}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </Stack>
                </DialogTitle>

                <DialogContent dividers>
                    {/* ── No session ── */}
                    {!detailSession && (
                        <Box>
                            <Alert severity="info" sx={{ alignItems: "center", mb: 2 }}>
                                No session found for this flight. Start one to enable sales recording.
                            </Alert>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="subtitle2">Inventory (optional)</Typography>
                                <ToggleButtonGroup
                                    value={inventoryMode}
                                    exclusive
                                    size="small"
                                    onChange={(_, v) => v && setInventoryMode(v)}
                                >
                                    <ToggleButton value="bulk" sx={{ px: 1, py: 0.25 }}>
                                        <ViewList 
                                            fontSize="small" 
                                            sx={{ mr: 0.5 }} 
                                        />
                                        <Typography variant="caption">
                                            All Products
                                        </Typography>
                                    </ToggleButton>
                                    <ToggleButton value="manual" sx={{ px: 1, py: 0.25 }}>
                                        <PlaylistAdd 
                                            fontSize="small" 
                                            sx={{ mr: 0.5 }} 
                                        />
                                        <Typography variant="caption">
                                            Add
                                        </Typography>
                                    </ToggleButton>                                    
                                </ToggleButtonGroup>
                            </Stack>
                            {inventoryMode === "manual" ? (
                                <InventoryEditor
                                    rows={inventoryRows}
                                    products={products}
                                    onUpdate={onUpdateInventoryRow}
                                    onAdd={() => setInventoryRows((p) => [...p, emptyInventoryRow()])}
                                    onRemove={(idx) => setInventoryRows((p) => p.filter((_, i) => i !== idx))}
                                />
                            ) : (
                                <BulkInventoryEditor
                                    products={products}
                                    rows={inventoryRows}
                                    onRowsChange={setInventoryRows}
                                />
                            )}
                        </Box>
                    )}

                    {/* ── Active session — view ── */}
                    {detailSession?.status === "active" && !editMode && !crewEditMode && (
                        <Box>
                            <Stack 
                                direction="row" 
                                justifyContent="space-between" 
                                alignItems="center" 
                                mb={1}
                            >
                                <Chip 
                                    label="Active" 
                                    color="success" 
                                    size="small" 
                                    icon={<CheckCircle />} 
                                />
                                <Stack direction="row" spacing={1}>
                                    <Button 
                                        size="small" 
                                        startIcon={<Edit />} 
                                        disabled={!isOnline} 
                                        onClick={onOpenCrewEdit}
                                    >
                                        Crew
                                    </Button>
                                    <Button 
                                        size="small" 
                                        startIcon={<Edit />} 
                                        disabled={!isOnline} 
                                        onClick={onOpenEdit}
                                    >
                                        Inventory
                                    </Button>
                                </Stack>
                            </Stack>
                            <SessionInfoTable session={detailSession} />
                            <CrewList crewMembers={detailSession.crewMembers} />
                            <InventoryList inventory={detailSession.inventory} />
                        </Box>
                    )}

                    {/* ── Active session — edit inventory ── */}
                    {detailSession?.status === "active" && editMode && (
                        <Box>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="subtitle2">
                                    Edit Inventory
                                </Typography>
                                <ToggleButtonGroup
                                    value={inventoryMode}
                                    exclusive
                                    size="small"
                                    onChange={(_, v) => v && setInventoryMode(v)}
                                >
                                    <ToggleButton value="bulk" sx={{ px: 1, py: 0.25 }}>
                                        <ViewList 
                                            fontSize="small" 
                                            sx={{ mr: 0.5 }} 
                                        />
                                        <Typography variant="caption">
                                            All Products
                                        </Typography>
                                    </ToggleButton>
                                    <ToggleButton value="manual" sx={{ px: 1, py: 0.25 }}>
                                        <PlaylistAdd 
                                            fontSize="small" 
                                            sx={{ mr: 0.5 }} 
                                        />
                                        <Typography variant="caption">
                                            Add
                                        </Typography>
                                    </ToggleButton>                                    
                                </ToggleButtonGroup>
                            </Stack>
                            {inventoryMode === "manual" ? (
                                <InventoryEditor
                                    rows={editRows}
                                    products={products}
                                    onUpdate={onUpdateEditRow}
                                    onAdd={() => setEditRows((p) => [...p, emptyInventoryRow()])}
                                    onRemove={(idx) => setEditRows((p) => p.filter((_, i) => i !== idx))}
                                />
                            ) : (
                                <BulkInventoryEditor
                                    products={products}
                                    rows={editRows}
                                    onRowsChange={setEditRows}
                                />
                            )}
                        </Box>
                    )}

                    {/* ── Active/Closed session — edit crew ── */}
                    {detailSession && crewEditMode && (
                        <Box>
                            <Typography variant="subtitle2" mb={1}>
                                Edit Crew
                            </Typography>
                            <CrewEditor
                                rows={crewRows}
                                availableCrew={availableCrew}
                                onUpdate={onUpdateCrewRow}
                                onAdd={(member) => setCrewRows((p) => [...p, member ?? { crewCode: "", crewName: "", post: "", base: "" }])}
                                onRemove={(idx) => setCrewRows((p) => p.filter((_, i) => i !== idx))}
                            />
                        </Box>
                    )}

                    {/* ── Closed session — view ── */}
                    {detailSession?.status === "closed" && !crewEditMode && (
                        <Box>
                            <Stack 
                                direction="row" 
                                alignItems="center" 
                                justifyContent="space-between" 
                                mb={1.5}
                            >
                                <Stack 
                                    direction="row" 
                                    alignItems="center" 
                                    spacing={1}
                                >
                                    <History fontSize="small" color="disabled" />
                                    <Chip 
                                        label="Closed" 
                                        size="small" 
                                        color="default" 
                                    />
                                    {detailSession.closedAt && (
                                        <Typography variant="caption" color="text.secondary">
                                            {format(new Date(detailSession.closedAt), "HH:mm dd/MM/yy")}
                                        </Typography>
                                    )}
                                </Stack>
                                <Button 
                                    size="small" 
                                    startIcon={<Edit />} 
                                    disabled={!isOnline} 
                                    onClick={onOpenCrewEdit}
                                >
                                    Crew
                                </Button>
                            </Stack>
                            <SessionInfoTable session={detailSession} />
                            <CrewList crewMembers={detailSession.crewMembers} />
                            <InventoryList inventory={detailSession.inventory} showSold />
                        </Box>
                    )}
                </DialogContent>

                <DialogActions>
                    {detailSession && crewEditMode && (
                        <>
                            <Button 
                                variant="outlined" 
                                color="secondary" 
                                onClick={() => setCrewEditMode(false)}
                            >
                                Cancel
                            </Button>
                            <Button 
                                variant="outlined" 
                                disabled={submitting} 
                                onClick={onSaveCrew}
                            >
                                {submitting ? "Saving…" : "Save"}
                            </Button>
                        </>
                    )}

                    {!detailSession && (
                        <>
                            <Button 
                                variant="outlined" 
                                color="secondary" 
                                onClick={onClose}
                            >
                                Cancel
                            </Button>
                            <Button 
                                variant="outlined" 
                                onClick={onStartSession} 
                                disabled={submitting}
                            >
                                {submitting ? "Starting…" : "Start"}
                            </Button>
                        </>
                    )}

                    {detailSession?.status === "active" && !editMode && !crewEditMode && (
                        <>
                            <Button 
                                variant="outlined" 
                                color="error" 
                                onClick={() => setCloseDialogOpen(true)}
                            >
                                Close Session
                            </Button>
                            <Button 
                                variant="outlined" 
                                color="secondary" 
                                onClick={onClose}
                            >
                                Cancel
                            </Button>
                        </>
                    )}

                    {detailSession?.status === "active" && editMode && (
                        <>
                            <Button 
                                variant="outlined" 
                                color="secondary" 
                                onClick={() => setEditMode(false)}
                            >
                                Cancel
                            </Button>
                            <Button 
                                variant="outlined" 
                                disabled={submitting} 
                                onClick={onSaveInventory}
                            >
                                {submitting ? "Saving…" : "Save"}
                            </Button>
                        </>
                    )}

                    {detailSession?.status === "closed" && !crewEditMode && (
                        <>
                            <Button 
                                variant="outlined" 
                                disabled={submitting} 
                                onClick={onReopenSession}
                            >
                                {submitting ? "Reopening…" : "Reopen Session"}
                            </Button>
                            <Button 
                                variant="outlined" 
                                color="secondary" 
                                onClick={onClose}
                            >
                                Close
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* ── Close Session Confirm ── */}
            <Dialog open={closeDialogOpen} onClose={() => setCloseDialogOpen(false)}>
                <DialogTitle>Close Session?</DialogTitle>
                <DialogContent>
                    <Typography>
                        All pending orders will be synced. The session will be marked as closed.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button 
                        variant="outlined" 
                        color="secondary" 
                        onClick={() => setCloseDialogOpen(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        disabled={submitting}
                        onClick={() => { setCloseDialogOpen(false); onCloseSession(); }}
                    >
                        {submitting ? "Closing…" : "Close"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default FlightDetailDialog;
